<script>
    import { preventDefault } from 'svelte/legacy';

import {
    fade
} from "svelte/transition";
import {
    modalState, hideModal
} from "@app/store/modalStore.svelte.js";
import {
    eventGroupState
} from '@app/store/eventGroupStore.svelte.js';
import {
    toast } from '@zerodevx/svelte-toast';

import _Tag from "@cclib/classes/tag.js";
import * as m from "@app/paraglide/messages.js";
import {
    tagState,
    tagColorMapping,
    loadAllTagSets,
    createTagSet as storeCreateTagSet,
    removeTagSet as storeRemoveTagSet,
    saveTagSet,
    toggleActiveSet,
    loadTagSets,
} from "@app/store/tagStore.svelte.js";

let selectedTagSet = $state(),
    selectedTag = $state(),
    tagTermInput = $state(""),
    tagDefinitionInput = $state(""),
    tagTypeInput = $state("Character");

let gettingTagSets = $state(loadAllTagSets());

async function createTagSet() {
    const newTagSet = await storeCreateTagSet(m.tags_manager_default_set_name());
    if (newTagSet) {
        toast.push(m.tags_manager_created_toast(), { classes: ["toast-success"] });
        selectedTagSet = newTagSet;
    }
}

async function removeTagSet(id) {
    try {
        await storeRemoveTagSet(id);
        toast.push(m.tags_manager_removed_toast(), { classes: ["toast-success"] });
        selectedTagSet = tagState.allSets.length > 0 ? tagState.allSets[0] : undefined;
    } catch (error) {
        toast.push(
            m.tags_manager_remove_failed_toast({ error: error.message }),
            { classes: ["toast-danger"] },
        );
    }
}

function selectTagSet(set) {
    selectedTagSet = set;
}

function toggleSet(set) {
    toggleActiveSet(set);
}

async function saveSet(set) {
    if (!set) return;

    try {
        await saveTagSet(set);
        toast.push(m.tags_manager_saved_toast(), { classes: ["toast-success"] });
    } catch (error) {
        console.log(m.tags_manager_error_save_set_log(), error);
        toast.push(
            m.tags_manager_error_save_set_toast({ error: error.message }),
            { classes: ["toast-danger"] },
        );
        gettingTagSets = loadAllTagSets();
    }
}

function editTagInSet(tag) {
    selectedTag = tag;
    tagTermInput = tag.term;
    tagTypeInput = tag.type;
    tagDefinitionInput = tag.definition;
}

function removeTagFromSet(id) {
    selectedTagSet.tags = selectedTagSet.tags.filter(tag => {
        return tag.id !== id;
    });

    /* Update Event Tags */
    eventGroupState.forEach(eventGroup => {
        eventGroup.events.forEach((event, i, events) => {
            if (event.tags) {
                events[i].tags = event.tags.filter(t => {
                    return t.id !== id;
                })
            }
        })
    });

    saveSet(selectedTagSet);
    resetForm();
}

function updateTag() {
    let tagIndex = selectedTagSet.tags.findIndex(tag => {
        return tag.id === selectedTag.id;
    });

    if (tagIndex > -1 && tagTermInput) {
        selectedTagSet.tags[tagIndex].term = tagTermInput;
        selectedTagSet.tags[tagIndex].type = tagTypeInput;
        selectedTagSet.tags[tagIndex].definition = tagDefinitionInput;
        selectedTagSet.tags[tagIndex].color = tagColorMapping[tagTypeInput];

        /* Update Event Tags */
        eventGroupState.forEach(eventGroup => {
            eventGroup.events.forEach((event, i, events) => {
                if (event.tags) {
                    let eventTagIndex = event.tags.findIndex(tgs => {
                        return tgs.id === selectedTagSet.tags[tagIndex].id
                    });

                    if (eventTagIndex > -1) {
                        events[i].tags[eventTagIndex].term = selectedTagSet.tags[tagIndex].term;
                        events[i].tags[eventTagIndex].type = selectedTagSet.tags[tagIndex].type;
                        events[i].tags[eventTagIndex].definition = selectedTagSet.tags[tagIndex].definition;
                    }
                }
            })
        });

        saveSet(selectedTagSet);
        resetForm();
    }
}

function insertTag() {
    let tag = {...(new _Tag({
        term: tagTermInput,
        type: tagTypeInput,
        definition: tagDefinitionInput,
        color: tagColorMapping[tagTypeInput],
    }))};

    selectedTagSet.tags = [...selectedTagSet.tags, tag];
    saveSet(selectedTagSet);
    resetForm();
}

function resetForm() {
    selectedTag = undefined;
    tagTermInput = "";
    tagDefinitionInput = "";
    tagTypeInput = "Character";
}

function handleClose() {
    // Refresh the Tags panel active sets on close so changes are reflected immediately
    loadTagSets(tagState.selectedId, true);
    hideModal();
}
</script>

<div transition:fade={{ duration: 100 }} 
    class="modal {modalState.selected === 'tagsManager' ? 'show d-block' : ''}" 
    role="dialog" 
    aria-labelledby="tagsManagerTitle"
    aria-modal="true"
    tabindex="-1" 
    id="TagSetManageModal">
    <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="tagsManagerTitle">
                    {m.tags_manager_title()}
                </h4>
                <button type="button" class="btn-close" aria-label={m.close()} onclick={handleClose}></button>
            </div>
            <div class="modal-body">
                <div class="row">
                    <div class="col-3 border-end border-secondary">
                        <div class="d-flex justify-content-end mb-2">
                            <button type="button" 
                                id="newTagSetBtn"
                                class="btn btn-primary btn-sm me-2" 
                                aria-label={m.tags_manager_create_set_label()}
                                onclick={() => createTagSet()}>
                                <i class="bi bi-plus-lg"></i> {m.new()}
                            </button>

                            <button type="button" class="btn btn-danger btn-sm {selectedTagSet ? 'text-white' : 'disabled'}" title={m.tags_manager_remove_set_title()} onclick={() => removeTagSet(selectedTagSet.id)}><i class="bi bi-x-lg"></i> {m.tags_manager_remove_set_button()}</button>
                        </div>
                        <nav aria-label={m.tags_manager_sets_nav_label()}>
                            <ul class="nav nav-pills flex-column" role="tablist">
                                {#await gettingTagSets}
                                <p>{m.tags_manager_loading_sets()}</p>
                                {:then}
                                {#each tagState.allSets as set (set.id)}
                                <li class="nav-item">
                                    <a class="nav-link {selectedTagSet && selectedTagSet.id === set.id ? 'active' : ''}" href="#!/" onclick={() => selectTagSet(set)}>{set.name} <input class="form-check-input float-end" type="checkbox" checked={tagState.activeSets.indexOf(set.id) > -1} onchange={() => toggleSet(set)} title={m.tags_manager_enable_disable_title()} /></a>
                                </li>
                                {:else}
                                <p class="text-muted small text-center my-5">{m.tags_manager_empty_sets()}</p>
                                {/each}
                                {/await}
                            </ul>
                        </nav>
                    </div>
                    <div class="col">
                        {#if selectedTagSet}
                        <form onsubmit={preventDefault(saveSet(selectedTagSet))}>
                            <div class="row mb-2">
                                <div class="col-12 mb-4">
                                    <label class="form-label text-warning fw-bold fs-5" for="TagTerm">{m.tags_manager_set_name_label()}</label>
                                    <input type="text" class="form-control" id="TagTermInput" bind:value={selectedTagSet.name}>
                                </div>
                                <p class="text-warning fw-bold fs-5">{m.tags_manager_edit_form_title()}</p>
                                <div class="col-8">
                                    <label class="form-label" for="TagTerm">{m.tags_manager_tag_term_label()}</label>
                                    <input type="text" class="form-control form-control-sm" id="TagTermInput" bind:value={tagTermInput}>
                                </div>
                                <div class="col-4">
                                    <label class="form-label" for="TagTypeInput">{m.tags_manager_tag_type_label()}</label>
                                    <select class="form-select form-select-sm" id="TagTypeInput" bind:value={tagTypeInput}>
                                        <option value="Character">{m.tags_manager_type_character()}</option>
                                        <option value="Location">{m.tags_manager_type_location()}</option>
                                        <option value="Organization">{m.tags_manager_type_organization()}</option>
                                        <option value="Phrase">{m.tags_manager_type_phrase()}</option>
                                        <option value="Other">{m.tags_manager_type_other()}</option>
                                    </select>
                                </div>
                                <div class="col-12 my-2">
                                    <label class="form-label" for="tagDefinitionInput">{m.tags_manager_definition_label()}</label>
                                    <textarea class="form-control" id="tagDefinitionInput" rows="3" bind:value={tagDefinitionInput}></textarea>
                                </div>
                            </div>
                            <div class="mb-3 text-end">
                                {#if selectedTag}
                                <button type="button" class="btn btn-warning" disabled={!tagTermInput} onclick={() => updateTag()}>{m.tags_manager_update_button()}</button>
                                {:else}
                                <button type="button" class="btn btn-primary" disabled={!tagTermInput} onclick={() => insertTag()}>{m.tags_manager_insert_button()}</button>
                                {/if}
                                <button type="button" class="btn btn-outline-dark" onclick={() => resetForm()}>{m.tags_manager_reset_button()}</button>
                            </div>
                        </form>
                        <div id="TagTableWrapper" class="table-responsive">
                            <table class="table table-sm table-striped">
                                <thead>
                                    <tr>
                                        <th scope="col">{m.tags_manager_table_index()}</th>
                                        <th scope="col">{m.tags_manager_table_term()}</th>
                                        <th scope="col">{m.tags_manager_table_type()}</th>
                                        <th style="width:125px;" scope="col">{m.tags_manager_table_actions()}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {#each selectedTagSet.tags as tag, index (tag.id)}
                                    <tr>
                                        <td>{index+1}</td>
                                        <td><p class="m-0">{tag.term}</p><p class="small text-muted">{tag.definition && tag.definition.length > 50 ? tag.definition.substring(0,50)+"..." : tag.definition}</p></td>
                                        <td><span class="badge bg-{tag.color}">{tag.type}</span></td>
                                        <td class='text-center'>
                                            <button aria-label={m.tags_manager_edit_tag_label()} class="btn btn-sm btn-info" type="button" title={m.tags_manager_edit_tag_title()} onclick={() => editTagInSet(tag)}><i class="bi bi-pencil-square"></i></button>
                                            <button aria-label={m.tags_manager_remove_tag_label()} class="btn btn-sm btn-danger" type="button" title={m.tags_manager_remove_tag_title()} onclick={() => removeTagFromSet(tag.id)}><i class="bi bi-trash"></i></button>
                                        </td>
                                    </tr>
                                    {/each}
                                </tbody>
                            </table>
                        </div>
                        {:else}
                        <p class="text-center text-muted lead my-5">{m.tags_manager_select_set_prompt()}</p>
                        {/if}
                    </div>
                </div>
            </div>
            <div class="modal-footer">
                {#if selectedTagSet}
                <button type="button" class="btn btn-primary" onclick={() => saveSet(selectedTagSet)} disabled={!selectedTagSet.name || selectedTagSet.name === "none"}>{m.tags_manager_save_changes_button()}</button>
                {:else}
                <button type="button" class="btn btn-light" onclick={handleClose}>{m.close()}</button>
                {/if}
            </div>
        </div>
    </div>
</div>

<style lang="css">
#TagTableWrapper {
    max-height: 30vh;
    overflow-y: scroll;
}
</style>
